/**
 * Delivery forecast (`/forecast`).
 *
 * Forward-looking view: projects sorted by effective delivery target proximity, with
 * a risk badge computed from parts current-stage distribution vs. days
 * remaining. Patch E22.
 *
 * Mental model:
 *   OVERDUE   target passed, deliverable not complete           (red)
 *   AT_RISK   target < 7 days, > 50% parts in procurement       (orange)
 *   WATCH     target < 30 days, any parts in procurement        (amber)
 *   ON_TRACK  parts mostly past procurement OR plenty of time   (neutral)
 *
 * Risk classification logic lives in `assessDeliveryRisk` (shared package)
 * so the same boundaries can be reused elsewhere (e.g. a dashboard tile).
 *
 * Filters out projects with no effectiveDeliveryTargetDate — without a target we
 * can't forecast anything. Closed projects also excluded.
 */
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { AlertTriangle, CalendarClock, CheckCircle2, Eye, MessageCircleReply, Search, X } from 'lucide-react';
import {
  assessDeliveryRisk,
  formatDate,
  formatRelative,
  todayPktIsoDate,
  type DeliveryRiskLevel,
} from '@procurement/shared';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorPanel } from '@/components/ui/error-panel';
import { Input } from '@/components/ui/form-field';
import { PageHeader } from '@/components/ui/page-header';
import { ProjectNumber } from '@/components/ui/project-number';
import { ProjectProgressBar } from '@/components/project-progress-bar';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Spinner } from '@/components/ui/spinner';
import { StatTile } from '@/components/ui/stat-tile';
import { LogCommunicationModal } from '@/components/attachments/log-communication-modal';
import { useLastContact, type LastContactSummary } from '@/lib/attachments-queries';
import type { AttachmentAnchor } from '@/lib/attachments-queries';
import { useProjectsList, type VisibilityRow } from '@/lib/visibility-queries';
import { usePageTitle } from '@/lib/use-page-title';

interface ForecastRow {
  project: VisibilityRow;
  level: DeliveryRiskLevel;
  daysToTarget: number;
}

const LEVEL_ORDER: Record<DeliveryRiskLevel, number> = {
  OVERDUE: 0,
  AT_RISK: 1,
  WATCH: 2,
  ON_TRACK: 3,
};

export function ForecastPage() {
  usePageTitle('Delivery forecast');
  const today = todayPktIsoDate();
  // Pull a wide page — same logic as the kanban: small dataset, cheaper to
  // sort and filter on the client than to add a dedicated endpoint.
  const projects = useProjectsList({ pageSize: 500, excludeClosed: true });

  const { rows, counts } = useMemo(() => {
    const items = projects.data?.items ?? [];
    const enriched: ForecastRow[] = [];
    for (const project of items) {
      if (!project.effectiveDeliveryTargetDate) continue; // can't forecast without a target
      const r = assessDeliveryRisk({
        effectiveDeliveryTargetDate: project.effectiveDeliveryTargetDate,
        buckets: project.partsProgress,
        today,
      });
      if (r.daysToTarget === null) continue;
      enriched.push({ project, level: r.level, daysToTarget: r.daysToTarget });
    }
    // Sort: by risk level (OVERDUE first), then by days-to-target ascending
    // so the nearest deliveries float up within each level.
    enriched.sort((a, b) => {
      const lvl = LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level];
      if (lvl !== 0) return lvl;
      return a.daysToTarget - b.daysToTarget;
    });
    const counts: Record<DeliveryRiskLevel, number> = {
      OVERDUE: 0,
      AT_RISK: 0,
      WATCH: 0,
      ON_TRACK: 0,
    };
    for (const r of enriched) counts[r.level] += 1;
    return { rows: enriched, counts };
  }, [projects.data?.items, today]);

  // Patch 2 — last contact + log-follow-up
  const projectIds = useMemo(() => rows.map((r) => r.project.projectId), [rows]);
  const lastContact = useLastContact(projectIds, []);
  const [logForAnchor, setLogForAnchor] = useState<AttachmentAnchor | null>(null);

  // Patch P14 — filters. Three knobs:
  //   - selectedRisk: click a tile to filter by that risk level (toggle)
  //   - search: free-text across project number / title / client name
  //   - filterClientId: client SearchableSelect (answers "which of Client
  //     X's deliveries are at risk")
  const [selectedRisk, setSelectedRisk] = useState<DeliveryRiskLevel | null>(null);
  const [search, setSearch] = useState('');
  const [filterClientId, setFilterClientId] = useState<string | null>(null);

  const clientOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of rows) {
      if (!seen.has(r.project.clientId)) seen.set(r.project.clientId, r.project.clientName);
    }
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (selectedRisk && r.level !== selectedRisk) return false;
      if (filterClientId && r.project.clientId !== filterClientId) return false;
      if (q) {
        const haystack = `${r.project.projectNumber} ${r.project.projectTitle} ${r.project.clientName}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [rows, selectedRisk, filterClientId, search]);

  const hasFilters = selectedRisk !== null || filterClientId !== null || search.trim().length > 0;
  const clearAll = () => {
    setSelectedRisk(null);
    setFilterClientId(null);
    setSearch('');
  };

  if (projects.isLoading) {
    return (
      <>
        <PageHeader
          title="Delivery forecast"
          subtitle="Projects ranked by effective delivery target proximity and risk. Sorted: overdue first, then nearest."
        />
        <div className="flex justify-center py-16">
          <Spinner className="h-6 w-6 text-secondary" />
        </div>
      </>
    );
  }
  // Patch 5 — explicit error state
  if (projects.isError || !projects.data) {
    return (
      <>
        <PageHeader
          title="Delivery forecast"
          subtitle="Projects ranked by effective delivery target proximity and risk. Sorted: overdue first, then nearest."
        />
        <ErrorPanel onRetry={() => projects.refetch()} />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Delivery forecast"
        subtitle="Projects ranked by effective delivery target proximity and risk. Sorted: overdue first, then nearest."
      />

      {/* Summary tiles by risk level — click to filter, click again to clear. */}
      <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="Overdue"
          value={counts.OVERDUE}
          tone={counts.OVERDUE > 0 ? 'danger' : 'neutral'}
          hint="Past target, not delivered"
          onClick={() => setSelectedRisk((cur) => (cur === 'OVERDUE' ? null : 'OVERDUE'))}
          active={selectedRisk === 'OVERDUE'}
        />
        <StatTile
          label="At risk"
          value={counts.AT_RISK}
          tone={counts.AT_RISK > 0 ? 'warning' : 'neutral'}
          hint="< 7 days, parts behind"
          onClick={() => setSelectedRisk((cur) => (cur === 'AT_RISK' ? null : 'AT_RISK'))}
          active={selectedRisk === 'AT_RISK'}
        />
        <StatTile
          label="Watch"
          value={counts.WATCH}
          tone={counts.WATCH > 0 ? 'warning' : 'neutral'}
          hint="< 30 days, work outstanding"
          onClick={() => setSelectedRisk((cur) => (cur === 'WATCH' ? null : 'WATCH'))}
          active={selectedRisk === 'WATCH'}
        />
        <StatTile
          label="On track"
          value={counts.ON_TRACK}
          tone={counts.ON_TRACK > 0 ? 'success' : 'neutral'}
          onClick={() => setSelectedRisk((cur) => (cur === 'ON_TRACK' ? null : 'ON_TRACK'))}
          active={selectedRisk === 'ON_TRACK'}
        />
      </div>

      <Card className="mb-3 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex w-64 items-center">
            <Search
              className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-tertiary"
              strokeWidth={1.5}
            />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search projects, clients…"
              className="pl-7"
            />
          </div>
          <SearchableSelect
            items={clientOptions}
            value={filterClientId}
            onChange={setFilterClientId}
            placeholder="Any client"
            clearable
            className="min-w-[200px]"
          />
          {hasFilters ? (
            <button
              type="button"
              onClick={clearAll}
              className="inline-flex items-center gap-1 text-xs text-secondary hover:text-primary"
            >
              <X className="h-3 w-3" strokeWidth={1.5} />
              Clear
            </button>
          ) : null}
          <span className="ml-auto text-xs text-secondary">
            {filteredRows.length} of {rows.length}
          </span>
        </div>
      </Card>

      {filteredRows.length === 0 ? (
        <Card>
          <EmptyState
            icon={<CalendarClock className="h-6 w-6" strokeWidth={1.5} />}
            title={hasFilters ? 'No matches' : 'No projects with effective delivery targets'}
            body={
              hasFilters
                ? 'No projects match the current filters. Try clearing them.'
                : 'Forecast shows only projects with a contract sign date and delivery duration set on an open project.'
            }
          />
        </Card>
      ) : (
        <Card bodyClassName="p-0">
          <ul className="divide-y divide-border-default">
            {filteredRows.map((row) => (
              <li key={row.project.projectId}>
                <ForecastRowItem
                  project={row.project}
                  level={row.level}
                  daysToTarget={row.daysToTarget}
                  lastContact={lastContact.data?.byProject[row.project.projectId] ?? null}
                  lastContactLoading={lastContact.isLoading}
                  onLogFollowUp={() =>
                    setLogForAnchor({
                      projectId: row.project.projectId,
                      projectItemId: null,
                      orderBatchId: null,
                      bankGuaranteeId: null,
                      inspectionEventId: null,
                      clientId: null,
                      supplierId: null,
                      masterDepartmentId: null,
                    })
                  }
                />
              </li>
            ))}
          </ul>
        </Card>
      )}

      {logForAnchor ? (
        <LogCommunicationModal
          open={true}
          onClose={() => setLogForAnchor(null)}
          projectId={logForAnchor.projectId ?? null}
          defaultAnchor={logForAnchor}
          defaultDirection="OUTBOUND"
        />
      ) : null}
    </>
  );
}

function ForecastRowItem({
  project,
  level,
  daysToTarget,
  lastContact,
  lastContactLoading,
  onLogFollowUp,
}: {
  project: VisibilityRow;
  level: DeliveryRiskLevel;
  daysToTarget: number;
  lastContact?: LastContactSummary | null;
  lastContactLoading?: boolean;
  onLogFollowUp?: () => void;
}) {
  return (
    <div className="flex flex-col gap-1 px-4 py-3 hover:bg-subtle">
      <div className="flex items-start gap-3">
        <Link
          to="/projects/$projectId"
          params={{ projectId: project.projectId }}
          className="min-w-0 flex-1"
        >
          <div className="flex items-center gap-2">
            <ProjectNumber number={project.projectNumber} size="sm" />
            <span className="truncate font-medium text-primary">{project.projectTitle}</span>
          </div>
          <div className="mt-0.5 truncate text-xs text-secondary">
            {project.clientName} · Effective target {formatDate(project.effectiveDeliveryTargetDate!)}
          </div>
          <div className="mt-1.5 max-w-md">
            <ProjectProgressBar buckets={project.partsProgress} variant="compact" />
          </div>
        </Link>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <RiskBadge level={level} />
          <DaysToTargetText daysToTarget={daysToTarget} level={level} />
        </div>
      </div>

      {(lastContact || onLogFollowUp || lastContactLoading) ? (
        <div className="flex items-center gap-2 pl-1 text-[11px] text-secondary">
          {lastContactLoading ? (
            <span
              aria-hidden
              className="inline-block h-3 w-32 animate-pulse rounded-sm bg-subtle"
            />
          ) : lastContact ? (
            <span title={lastContact.subject}>
              Last contact:{' '}
              <span className="text-primary">
                {formatRelative(lastContact.occurredAt)}
              </span>
              {' — '}
              {FORECAST_COMM_SHORT[lastContact.commType]}
              {lastContact.counterpartyName ? ` (${lastContact.counterpartyName})` : ''}
            </span>
          ) : (
            <span className="italic text-tertiary">No contact logged.</span>
          )}
          {onLogFollowUp && !lastContactLoading ? (
            <button
              type="button"
              onClick={onLogFollowUp}
              className="ml-auto inline-flex items-center gap-1 rounded-sm border border-border-default px-1.5 py-0.5 text-[10px] text-secondary hover:border-accent hover:text-accent-strong"
            >
              <MessageCircleReply className="h-3 w-3" strokeWidth={1.5} />
              {lastContact ? 'Log follow-up' : 'Log first follow-up'}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const FORECAST_COMM_SHORT: Record<string, string> = {
  PHONE_CALL: 'Phone call',
  MEETING: 'Meeting',
  LETTER_RECEIVED: 'Letter in',
  LETTER_SENT: 'Letter out',
  EMAIL: 'Email',
  WHATSAPP: 'WhatsApp',
  SMS: 'SMS',
  INTERNAL_NOTE: 'Note',
};

function RiskBadge({ level }: { level: DeliveryRiskLevel }) {
  switch (level) {
    case 'OVERDUE':
      return (
        <span className="inline-flex items-center gap-1 rounded-sm bg-danger/10 px-2 py-0.5 text-xs font-medium text-danger-strong">
          <AlertTriangle className="h-3 w-3" strokeWidth={1.5} />
          Overdue
        </span>
      );
    case 'AT_RISK':
      return (
        <span className="inline-flex items-center gap-1 rounded-sm bg-warning-strong/10 px-2 py-0.5 text-xs font-medium text-warning-strong">
          <AlertTriangle className="h-3 w-3" strokeWidth={1.5} />
          At risk
        </span>
      );
    case 'WATCH':
      return (
        <span className="inline-flex items-center gap-1 rounded-sm bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
          <Eye className="h-3 w-3" strokeWidth={1.5} />
          Watch
        </span>
      );
    case 'ON_TRACK':
      return (
        <span className="inline-flex items-center gap-1 rounded-sm bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
          <CheckCircle2 className="h-3 w-3" strokeWidth={1.5} />
          On track
        </span>
      );
  }
}

function DaysToTargetText({
  daysToTarget,
  level,
}: {
  daysToTarget: number;
  level: DeliveryRiskLevel;
}) {
  if (daysToTarget < 0) {
    const days = Math.abs(daysToTarget);
    return (
      <span className="text-xs tabular-nums text-danger-strong">
        {days}d overdue
      </span>
    );
  }
  if (daysToTarget === 0) {
    return <span className="text-xs tabular-nums text-warning-strong">Today</span>;
  }
  return (
    <span
      className={
        level === 'AT_RISK' || level === 'WATCH'
          ? 'text-xs tabular-nums text-secondary'
          : 'text-xs tabular-nums text-secondary'
      }
    >
      in {daysToTarget}d
    </span>
  );
}
